import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Motion";
import { ArrowUpRight } from "lucide-react";
import BeePattern from "@/components/BeePattern";

const VIDEOS = {
  "bluetenhonig": "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/p5csshch_Bl%C3%BCtenhonig_6.mp4",
  "waldhonig": "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/ru7yv30y_Waldhonig_6.mp4",
  "spezial-exklusiv": "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/nt206ccz_MILIcitosehonig_6.mp4",
  "vanilla-alpine-honey": "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/6hty8hys_Vanillahoney_VD.mp4",
};

export default function SensorySection() {
  const { lang } = useI18n();

  const content = {
    de: {
      eyebrow: "Die Kollektion",
      title: "Sensorisches Profil",
      rowsLabel: [
        { key: "taste", label: "Geschmack" },
        { key: "nuances", label: "Nuancen" },
        { key: "color", label: "Farbe" },
        { key: "texture", label: "Textur" },
      ],
      cta: "Zum Produkt",
      cards: [
        {
          badge: "Signature Release",
          slug: "bluetenhonig",
          name: "PURE ALPINE BLÜTENHONIG",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/06iekuv3_Bl%C3%BCtenhonig_1.png",
          swatch: "#E4C46A",
          data: {
            taste: "Mild-floral",
            nuances: "Alpenklee & Bergminze",
            color: "Heller Bernstein",
            texture: "Samtig-fließend",
          },
        },
        {
          badge: "Limited Reserve",
          slug: "waldhonig",
          name: "PURE ALPINE WALDHONIG",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/zgbffl6d_Waldhonig_3.png",
          swatch: "#5C2E14",
          data: {
            taste: "Harzig-malzig",
            nuances: "Fichtennadel & Karamell",
            color: "Dunkles Kastanienbraun",
            texture: "Dicht & viskos",
          },
        },
        {
          badge: "Limited Release",
          slug: "spezial-exklusiv",
          name: "SPEZIAL EXKLUSIVER HONIG · MILIZITOSE",
          weight: "250 g ℮",
          image: "/images/products/spezial-exklusiv.jpg",
          swatch: "#7A4A12",
          data: {
            taste: "Würzig-malzig, tief",
            nuances: "Harz & Wald",
            color: "Dunkler Bernstein",
            texture: "Fest, fein kristallin",
          },
        },
        {
          badge: "Small Batch",
          slug: "vanilla-alpine-honey",
          name: "VANILLE ALPENHONIG",
          weight: "250 g ℮",
          image: "/images/products/vanilla-alpine-honey.jpg",
          swatch: "#D9A441",
          data: {
            taste: "Warm-vanillig",
            nuances: "Bourbon-Vanille & Alpenblüten",
            color: "Tiefes Gold",
            texture: "Cremig-glatt",
          },
        },
      ],
    },
    en: {
      eyebrow: "The Collection",
      title: "Sensory Profile",
      rowsLabel: [
        { key: "taste", label: "Taste" },
        { key: "nuances", label: "Nuances" },
        { key: "color", label: "Colour" },
        { key: "texture", label: "Texture" },
      ],
      cta: "View product",
      cards: [
        {
          badge: "Signature Release",
          slug: "bluetenhonig",
          name: "PURE ALPINE BLOSSOM HONEY",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/06iekuv3_Bl%C3%BCtenhonig_1.png",
          swatch: "#E4C46A",
          data: {
            taste: "Mild-floral",
            nuances: "Alpine clover & mountain mint",
            color: "Light amber",
            texture: "Velvet, flowing",
          },
        },
        {
          badge: "Limited Reserve",
          slug: "waldhonig",
          name: "PURE ALPINE FOREST HONEY",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/zgbffl6d_Waldhonig_3.png",
          swatch: "#5C2E14",
          data: {
            taste: "Resinous, malty",
            nuances: "Spruce needle & caramel",
            color: "Deep chestnut",
            texture: "Dense & viscous",
          },
        },
        {
          badge: "Limited Release",
          slug: "spezial-exklusiv",
          name: "SPECIAL EXCLUSIVE HONEY · MELEZITOSE",
          weight: "250 g ℮",
          image: "/images/products/spezial-exklusiv.jpg",
          swatch: "#7A4A12",
          data: {
            taste: "Spicy, malty, deep",
            nuances: "Resin & woodland",
            color: "Dark amber",
            texture: "Firm, finely crystalline",
          },
        },
        {
          badge: "Small Batch",
          slug: "vanilla-alpine-honey",
          name: "VANILLA ALPINE HONEY",
          weight: "250 g ℮",
          image: "/images/products/vanilla-alpine-honey.jpg",
          swatch: "#D9A441",
          data: {
            taste: "Warm vanilla",
            nuances: "Bourbon vanilla & alpine blossom",
            color: "Deep gold",
            texture: "Creamy, smooth",
          },
        },
      ],
    },
    it: {
      eyebrow: "La Collezione",
      title: "Profilo Sensoriale",
      rowsLabel: [
        { key: "taste", label: "Gusto" },
        { key: "nuances", label: "Sfumature" },
        { key: "color", label: "Colore" },
        { key: "texture", label: "Tessitura" },
      ],
      cta: "Vai al prodotto",
      cards: [
        {
          badge: "Signature Release",
          slug: "bluetenhonig",
          name: "MIELE MILLEFIORI ALPINO CRUDO",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/06iekuv3_Bl%C3%BCtenhonig_1.png",
          swatch: "#E4C46A",
          data: {
            taste: "Delicato, floreale",
            nuances: "Trifoglio alpino e menta di montagna",
            color: "Ambra chiaro",
            texture: "Vellutata, fluida",
          },
        },
        {
          badge: "Limited Reserve",
          slug: "waldhonig",
          name: "MIELE DI BOSCO ALPINO CRUDO",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/zgbffl6d_Waldhonig_3.png",
          swatch: "#5C2E14",
          data: {
            taste: "Resinoso, maltato",
            nuances: "Ago di abete e caramello",
            color: "Castagno profondo",
            texture: "Densa e viscosa",
          },
        },
        {
          badge: "Limited Release",
          slug: "spezial-exklusiv",
          name: "MIELE SPECIALE ESCLUSIVO · MELEZITOSE",
          weight: "250 g ℮",
          image: "/images/products/spezial-exklusiv.jpg",
          swatch: "#7A4A12",
          data: {
            taste: "Speziato, maltato, profondo",
            nuances: "Resina e sottobosco",
            color: "Ambra scuro",
            texture: "Compatta, finemente cristallina",
          },
        },
        {
          badge: "Small Batch",
          slug: "vanilla-alpine-honey",
          name: "MIELE ALPINO ALLA VANIGLIA",
          weight: "250 g ℮",
          image: "/images/products/vanilla-alpine-honey.jpg",
          swatch: "#D9A441",
          data: {
            taste: "Caldo, vanigliato",
            nuances: "Vaniglia bourbon e fiori alpini",
            color: "Oro intenso",
            texture: "Cremosa, liscia",
          },
        },
      ],
    },
    ja: {
      eyebrow: "コレクション",
      title: "センソリープロファイル",
      rowsLabel: [
        { key: "taste", label: "味わい" },
        { key: "nuances", label: "ニュアンス" },
        { key: "color", label: "色" },
        { key: "texture", label: "質感" },
      ],
      cta: "商品を見る",
      cards: [
        {
          badge: "Signature Release",
          slug: "bluetenhonig",
          name: "PURE ALPINE BLOSSOM HONEY",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/06iekuv3_Bl%C3%BCtenhonig_1.png",
          swatch: "#E4C46A",
          data: {
            taste: "マイルドでフローラル",
            nuances: "アルペンクローバーと山ミント",
            color: "明るい琥珀色",
            texture: "なめらかで流れるよう",
          },
        },
        {
          badge: "Limited Reserve",
          slug: "waldhonig",
          name: "PURE ALPINE FOREST HONEY",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/zgbffl6d_Waldhonig_3.png",
          swatch: "#5C2E14",
          data: {
            taste: "樹脂感と麦芽感",
            nuances: "トウヒの針葉とキャラメル",
            color: "深い栗色",
            texture: "濃密で粘性",
          },
        },
        {
          badge: "Limited Release",
          slug: "spezial-exklusiv",
          name: "SPECIAL EXCLUSIVE HONEY · MELEZITOSE",
          weight: "250 g ℮",
          image: "/images/products/spezial-exklusiv.jpg",
          swatch: "#7A4A12",
          data: {
            taste: "スパイシーで麦芽感、深み",
            nuances: "樹脂と森の香り",
            color: "ダークアンバー",
            texture: "しっかり、きめ細かな結晶",
          },
        },
        {
          badge: "Small Batch",
          slug: "vanilla-alpine-honey",
          name: "VANILLA ALPINE HONEY",
          weight: "250 g ℮",
          image: "/images/products/vanilla-alpine-honey.jpg",
          swatch: "#D9A441",
          data: {
            taste: "温かなバニラ",
            nuances: "バーボンバニラとアルプスの花々",
            color: "深いゴールド",
            texture: "クリーミーでなめらか",
          },
        },
      ],
    },
    zh: {
      eyebrow: "产品系列",
      title: "感官档案",
      rowsLabel: [
        { key: "taste", label: "口感" },
        { key: "nuances", label: "细腻层次" },
        { key: "color", label: "色泽" },
        { key: "texture", label: "质地" },
      ],
      cta: "查看产品",
      cards: [
        {
          badge: "Signature Release",
          slug: "bluetenhonig",
          name: "PURE ALPINE BLOSSOM HONEY",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/06iekuv3_Bl%C3%BCtenhonig_1.png",
          swatch: "#E4C46A",
          data: {
            taste: "温和花香",
            nuances: "高山三叶草与山薄荷",
            color: "浅琥珀色",
            texture: "如天鹅绒般流动",
          },
        },
        {
          badge: "Limited Reserve",
          slug: "waldhonig",
          name: "PURE ALPINE FOREST HONEY",
          weight: "500 g ℮",
          image: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/zgbffl6d_Waldhonig_3.png",
          swatch: "#5C2E14",
          data: {
            taste: "树脂与麦芽香",
            nuances: "云杉针叶与焦糖",
            color: "深栗色",
            texture: "浓稠绵密",
          },
        },
        {
          badge: "Limited Release",
          slug: "spezial-exklusiv",
          name: "SPECIAL EXCLUSIVE HONEY · MELEZITOSE",
          weight: "250 g ℮",
          image: "/images/products/spezial-exklusiv.jpg",
          swatch: "#7A4A12",
          data: {
            taste: "辛香麦芽,深沉",
            nuances: "树脂与森林气息",
            color: "深琥珀色",
            texture: "紧实,细结晶",
          },
        },
        {
          badge: "Small Batch",
          slug: "vanilla-alpine-honey",
          name: "VANILLA ALPINE HONEY",
          weight: "250 g ℮",
          image: "/images/products/vanilla-alpine-honey.jpg",
          swatch: "#D9A441",
          data: {
            taste: "温暖香草",
            nuances: "波旁香草与阿尔卑斯山花",
            color: "深金色",
            texture: "奶油般顺滑",
          },
        },
      ],
    },
  }[lang] || null;

  const data = content;

  return (
    <section className="relative bg-[#0A0A0A] py-32 lg:py-40 border-t border-white/5 overflow-hidden" data-testid="sensory-section">
      <BeePattern opacity={0.2} size="500px" vignette="linear" />
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12">
        <FadeUp className="text-center mb-20 lg:mb-24">
          <div className="font-display text-[10px] tracking-[0.45em] text-gold mb-6">/ {data.eyebrow}</div>
          <h2 className="font-editorial italic font-light text-4xl sm:text-5xl lg:text-6xl text-cream leading-[1.05] max-w-3xl mx-auto">
            {data.title}
          </h2>
          <div className="w-16 h-px bg-gold/60 mx-auto mt-10" />
        </FadeUp>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {data.cards.map((c, i) => (
            <motion.article
              key={c.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.1 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#0d0d0d] border border-white/10 hover:border-gold/60 transition-colors duration-700 clip-frame overflow-hidden flex flex-col lg:flex-row"
              data-testid={`sensory-card-${c.slug}`}
            >
              {/* Image */}
              <div className="lg:w-2/5 relative overflow-hidden aspect-square lg:aspect-auto">
                <div className="absolute inset-0 hero-glow z-0" />
                <video autoPlay muted loop playsInline className="relative z-[1] w-full h-full object-cover" data-testid={`sensory-video-${c.slug}`}>
                  <source src={`/videos/${c.slug}.webm`} type="video/webm" />
                  <source src={VIDEOS[c.slug]} type="video/mp4" />
                </video>
                <div className="absolute top-5 left-5 border border-gold/60 bg-black/50 backdrop-blur-sm px-3 py-1.5 font-display text-[9px] tracking-[0.3em] text-gold">
                  {c.badge}
                </div>
              </div>

              {/* Info */}
              <div className="lg:w-3/5 p-8 lg:p-12 flex flex-col justify-between">
                <div>
                  <div className="font-display text-[9px] tracking-[0.35em] text-cream/40 mb-2" style={{ textTransform: "none" }}>
                    0{i + 1} · {c.weight}
                  </div>
                  <h3 className="font-display tracking-[0.14em] text-cream text-lg lg:text-xl leading-snug mb-8">
                    {c.name}
                  </h3>

                  <ul className="divide-y divide-white/10 border-y border-white/10">
                    {data.rowsLabel.map((row) => (
                      <li key={row.key} className="grid grid-cols-2 py-3 items-center">
                        <span className="font-display text-[10px] tracking-[0.35em] text-gold/80">{row.label}</span>
                        <span className="font-editorial italic text-cream text-base leading-tight flex items-center gap-2">
                          {row.key === "color" && (
                            <span
                              aria-hidden="true"
                              className="inline-block w-3 h-3 border border-white/20 shrink-0"
                              style={{ background: c.swatch }}
                            />
                          )}
                          {c.data[row.key]}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to={`/product/${c.slug}`}
                  data-testid={`sensory-cta-${c.slug}`}
                  className="mt-10 inline-flex items-center gap-2 self-start font-display text-[10px] tracking-[0.35em] text-gold hover:text-gold-light link-underline group/link"
                >
                  → {data.cta}
                  <ArrowUpRight className="w-3 h-3 group-hover/link:rotate-45 transition-transform" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
